import { useTranslation } from 'react-i18next';

import type { BarChartDatum } from '~/components/BarChart/BarChart';
import { BarChart } from '~/components/BarChart/BarChart';
import { cn } from '~/lib/cn';

import { mockDSPStreamingData } from '../../mockData/mockData';
import { PerformanceModuleHeader } from '../../PerformanceModuleHeader';
import { formatMillionsNumber } from '../../utils';
import type { StreamType } from '../TrackStreamsGraph/TrackStreamsGraph';
import { DSPIcon } from './DSPIcon';
import { DSPTooltip } from './DSPTooltip';

export interface DSPTableProps {
  streamType?: StreamType;
  territory: string;
  startDate: string;
  endDate: string;
  className?: string;
}

export function DSPTable({
  streamType = 'streams',
  territory,
  startDate,
  endDate,
  className = '',
}: DSPTableProps) {
  const { t } = useTranslation('roster');
  const { t: nameTranslation } = useTranslation('names');

  const totalStreams = mockDSPStreamingData.total_streams;
  const barChartData: BarChartDatum[] = mockDSPStreamingData.streams_by_dsp.map(
    (data) => {
      const { source, total, change } = data;
      return {
        percentageValues: [Math.round((total / totalStreams) * 100)],
        categoryIcon: <DSPIcon type={source} className="w-md" />,
        categoryLabel: nameTranslation(source),
        valueLabel: formatMillionsNumber(total),
        delta: Math.round((change / total) * 100),
        tooltip: {
          contentClassName: 'bg-level-03',
          content: (
            <DSPTooltip
              dsp={source}
              data={data}
              totalStreams={totalStreams}
              streamType={streamType}
            />
          ),
          side: 'right',
          sideOffset: 8,
          children: null,
        },
      };
    },
  );

  return (
    <div
      className={cn(
        'flex flex-col gap-sm rounded-sm border-px border-solid p-lg duration-300 ease-out bg-level-01 border-emphasis-subtle hover:shadow-xl hover:bg-level-02 hover:border-emphasis-strong',
        className,
      )}
    >
      <PerformanceModuleHeader
        header={t('trackDetails.dsp')}
        territory={territory}
        startDate={startDate}
        endDate={endDate}
      />
      <div className="flex-1 px-md">
        <BarChart data={barChartData} />
      </div>
    </div>
  );
}

DSPTable.displayName = 'DSPTable';
